
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import AuthCallback from "./pages/AuthCallback";
import NotFound from "./pages/NotFound";
import { ThemeProvider } from "@/context/ThemeContext";
import { AuthProvider } from "@/context/AuthContext";
import { useAuth } from "@/context/AuthContext";
import Profile from "./pages/Profile";
import { OrganizationProvider } from "@/context/OrganizationContext";
import Componentes from "./components/pages/Componentes";
import { SidebarProvider } from "@/context/SidebarContext";
import { Layout } from "@/components/layout/Layout";
import DiagramEditorPage from "./pages/DiagramEditor";
import ComponentTypes from "./components/pages/ComponentTypes";
import DiagramDetails from "./pages/DiagramDetails";
import ComponentDetails from "./pages/ComponentDetails";
import Planos from "./pages/Planos";
import { Loading } from "@/components/ui/loading";

const queryClient = new QueryClient();

// Componente para proteger rotas
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading } = useAuth();
  
  if (isLoading) {
    return <Loading />;
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  return <>{children}</>;
};

const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/auth" element={<Auth />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/" element={
        <ProtectedRoute>
          <SidebarProvider>
            <Layout />
          </SidebarProvider>
        </ProtectedRoute>
      } />
      <Route path="/leia-me" element={
        <ProtectedRoute>
          <SidebarProvider>
            <Layout />
          </SidebarProvider>
        </ProtectedRoute>
      } />
      <Route path="/diagramas" element={
        <ProtectedRoute>
          <SidebarProvider>
            <Layout />
          </SidebarProvider>
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <Profile />
        </ProtectedRoute>
      } />
      <Route path="/componentes" element={
        <ProtectedRoute>
          <SidebarProvider>
            <Layout />
          </SidebarProvider>
        </ProtectedRoute>
      } />
      <Route path="/tipos-componentes" element={
        <ProtectedRoute>
          <SidebarProvider>
            <Layout />
          </SidebarProvider>
        </ProtectedRoute>
      } />
      <Route path="/configuracoes" element={
        <ProtectedRoute>
          <SidebarProvider>
            <Layout />
          </SidebarProvider>
        </ProtectedRoute>
      } />
      <Route path="/diagrama/:id" element={
        <ProtectedRoute>
          <DiagramEditorPage />
        </ProtectedRoute>
      } />
      <Route path="/diagrama-detalhes/:id" element={
        <ProtectedRoute>
          <SidebarProvider>
            <Layout />
          </SidebarProvider>
        </ProtectedRoute>
      } />
      <Route path="/componente-detalhes/:id" element={
        <ProtectedRoute>
          <SidebarProvider>
            <Layout />
          </SidebarProvider>
        </ProtectedRoute>
      } />
      <Route path="/planos" element={
        <ProtectedRoute>
          <SidebarProvider>
            <Layout />
          </SidebarProvider>
        </ProtectedRoute>
      } />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <AuthProvider>
        <OrganizationProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <AppRoutes />
            </BrowserRouter>
          </TooltipProvider>
        </OrganizationProvider>
      </AuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
